<script setup lang="ts">
  import { type ModelRef, watch, type Ref, ref, computed, onMounted } from 'vue'
  import { useLocationStore } from '@/store/location.store.ts'
  import { storeToRefs } from 'pinia'
  import type { JourneyLocationType } from '@/store/location.store.ts'
  import {
    isValid,
    validate,
    type CreateLocalLocation,
    type CreateLocationErrors,
    type Location,
  } from '@/model/location.model.ts'
  import CreateLocationForm from '@/components/LocationForm.vue'
  import type { Organization } from '@/types/organization'
  import { Modal, DropDown, type Nullish } from '@logistiek-zonder-papier/lzp-library'
  import { scrollToTopError } from '@/utils/validation'
  import { decodeHtmlEntities } from '@/utils/html-decoder'

  const locationStore = useLocationStore()

  const {
    organization,
    locationType,
    label = 'Locatie',
  } = defineProps<{
    organization: Organization
    locationType: JourneyLocationType
    label?: string
    error?: string | undefined
  }>()

  const show: Ref<boolean> = ref(false)
  const { locations } = storeToRefs(locationStore)
  const createLocation: Ref<Partial<CreateLocalLocation>> = ref({})

  const selectedLocation: ModelRef<Location | Nullish> = defineModel<Location | Nullish>({
    default: null,
  })

  const filteredLocation = computed<Location[]>(() => {
    return locations.value.filter(location => location.organisatie.id == organization?.id)
  })

  const submitAttempted = ref(false)
  const errors = computed<CreateLocationErrors>(() => validate(createLocation.value))
  const visibleErrors = computed(() => (submitAttempted.value ? errors.value : {}))

  async function submitNewLocation() {
    submitAttempted.value = true
    if (!isValid(createLocation.value)) {
      scrollToTopError()
      return
    }

    if (organization == null) return

    createLocation.value.organisatie.organisatieUUID = organization.organisatieUUID
    const resultLocation = await locationStore.createLocation(createLocation.value)
    await locationStore.fetchLocationsForOrganization(organization.id, locationType, true)
    show.value = false
    selectedLocation.value = resultLocation
  }

  watch(
    () => organization?.id,
    () => {
      if (organization?.id == null) return
      locationStore.fetchLocationsForOrganization(organization?.id, locationType)
    }
  )

  onMounted(() => {
    if (organization?.id == null) return
    locationStore.fetchLocationsForOrganization(organization?.id, locationType)
  })

  function onClose(close: () => void) {
    submitAttempted.value = false
    createLocation.value = {}
    close()
  }
</script>

<template>
  <template v-if="organization != null">
    <drop-down
      v-model="selectedLocation"
      :data="filteredLocation"
      :enable-search="true"
      :label="label"
      :allow-new="true"
      @add="show = true"
      :error="error"
      :data-validation-error="!!error"
      required
    >
      <template #empty>Selecteer adres gegevens</template>
      <template #item="{ item }">
        <span class="selection-option flex-container">
          <span class="item-name">
            {{ item.naam }}
          </span>
          <span class="item-name">
            {{ item.vestigingsNummer }}
          </span>
          <span class="item-name">
            {{ item.straatnaam }} {{ item.huisNummer }} {{ item.toevoeging }}
            {{ item.postcode }}
          </span>
          <span class="item-name">
            {{ decodeHtmlEntities(item.land) }}
          </span></span
        >
      </template>
    </drop-down>
    <Modal v-model:show="show">
      <template #header>Locatie toevoegen </template>
      <CreateLocationForm
        v-model="createLocation"
        :errors="visibleErrors"
        :organization="organization"
      />
      <template #footer="{ close }">
        <button class="btn btn-primary-outline" form="ignore-form" @click="onClose(close)">
          Sluiten
        </button>
        <button class="btn btn-primary" form="ignore-form" @click="submitNewLocation">
          Opslaan
        </button>
      </template>
    </Modal>
  </template>
</template>

<style scoped>
  .item-name {
    flex: 1 0 0;
    text-align: left;
  }

  .flex-container {
    display: flex;
    justify-content: space-between;
  }
</style>
